import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/tailwind.utils'

const badgeVariants = cva(
  'inline-flex items-center rounded-full border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground shadow',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground shadow',
        outline: 'text-foreground',
        // Figma "Resilience-Explorer-Library" badge colour set — https://www.figma.com/design/bDKp3l8zHhIhR9amgzls1u/Resilience-Explorer-Library?node-id=5951-343
        // BADGE_COLOR_ORDER below lists these in the exact order shown there.
        green: 'border-transparent bg-green-100 text-darkBlue-600',
        orange: 'border-transparent bg-orange-100 text-darkBlue-600',
        neutral: 'border-transparent bg-neutral-100 text-darkBlue-600',
        yellow: 'border-transparent bg-yellow-100 text-darkBlue-600',
        pink: 'border-transparent bg-pink-100 text-darkBlue-600',
        lime: 'border-transparent bg-lime-100 text-darkBlue-600',
        teal: 'border-transparent bg-teal-100 text-darkBlue-600',
        violet: 'border-transparent bg-violet-100 text-darkBlue-600',
        red: 'border-transparent bg-[#ffe2e2] text-darkBlue-600',
        softCream: 'border-transparent bg-softCream-100 text-darkBlue-600',
        purple: 'border-transparent bg-purple-100 text-darkBlue-600',
        sageGreen: 'border-transparent bg-sageGreen-100 text-darkBlue-600',
        amber: 'border-transparent bg-amber-100 text-darkBlue-600',
        dustyRose: 'border-transparent bg-dustyRose-100 text-darkBlue-600',
        emerald: 'border-transparent bg-emerald-100 text-darkBlue-600',
        fuchsia: 'border-transparent bg-fuchsia-100 text-darkBlue-600',
        rose: 'border-transparent bg-rose-100 text-darkBlue-600',
        zinc: 'border-transparent bg-zinc-100 text-darkBlue-600',
      },
      size: {
        xs: 'px-1.5 py-0 text-[10px]',
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1.5 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  },
)

// The 18 Figma badge colours, in the order shown in the Figma frame — for callers that need to
// assign a distinct colour per item (e.g. one per role) rather than a single fixed variant.
export const BADGE_COLOR_ORDER = [
  'green',
  'orange',
  'neutral',
  'yellow',
  'pink',
  'lime',
  'teal',
  'violet',
  'red',
  'softCream',
  'purple',
  'sageGreen',
  'amber',
  'dustyRose',
  'emerald',
  'fuchsia',
  'rose',
  'zinc',
] as const satisfies readonly NonNullable<VariantProps<typeof badgeVariants>['variant']>[]

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, ...props }, ref) => (
    <div ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props} />
  ),
)
Badge.displayName = 'Badge'

export { Badge, badgeVariants }
